import React, { useState, useEffect, useMemo } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';
import { useAppStore } from '../../store';
import { formatCurrency } from '../../utils/formatters';
import ErrorModal from './ErrorModal';
import { getCurrentArgentinaISOString, formatArgentinaDate } from '../../utils/dateUtils';
import { supabase } from '../../lib/supabase';

interface CreditNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityId: string;
  entityName: string;
  entityType: 'client' | 'supplier';
}

const CreditNoteModal: React.FC<CreditNoteModalProps> = ({
  isOpen,
  onClose,
  entityId,
  entityName,
}) => {
  const { payments, addPayment } = useAppStore();
  const [selectedInvoice, setSelectedInvoice] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(getCurrentArgentinaISOString());
  const [method, setMethod] = useState<'cash' | 'transfer' | 'card' | 'check' | 'mercadopago'>('cash');
  const [reason, setReason] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingInvoices, setPendingInvoices] = useState<any[]>([]);
  const [pendingLoading, setPendingLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setDate(getCurrentArgentinaISOString());
      setAmount('');
      setSelectedInvoice('');
      setMethod('cash');
      setReason('');
      setShowAdvanced(false);
      fetchPendingInvoices();
    }
  }, [isOpen, entityId]);

  const fetchPendingInvoices = async () => {
    setPendingLoading(true);
    try {
      const { data } = await supabase
        .from('invoices')
        .select('*')
        .eq('entity_id', entityId)
        .gt('balance', 0)
        .eq('voided', false)
        .order('date', { ascending: false });
      setPendingInvoices(data || []);
      if (data?.length === 1) {
        setSelectedInvoice(data[0].id);
        const paid = payments.filter(p => p.invoice_id === data[0].id && !p.voided).reduce((s, p) => s + p.amount, 0);
        setAmount(Math.max(0, data[0].total - paid).toString());
      }
    } catch {
      setPendingInvoices([]);
    } finally {
      setPendingLoading(false);
    }
  };

  const selectedInv = useMemo(() => pendingInvoices.find(i => i.id === selectedInvoice), [pendingInvoices, selectedInvoice]);
  const balance = useMemo(() => {
    if (!selectedInv) return 0;
    const paid = payments.filter(p => p.invoice_id === selectedInv.id && !p.voided).reduce((s, p) => s + p.amount, 0);
    return Math.max(0, selectedInv.total - paid);
  }, [selectedInv, payments]);

  const handleInvoiceChange = (id: string) => {
    setSelectedInvoice(id);
    const inv = pendingInvoices.find(i => i.id === id);
    if (inv) {
      const paid = payments.filter(p => p.invoice_id === inv.id && !p.voided).reduce((s, p) => s + p.amount, 0);
      setAmount(Math.max(0, inv.total - paid).toString());
    } else setAmount('');
  };

  const invoiceOptions = useMemo(() => {
    if (pendingLoading) return [{ value: '', label: 'Cargando...' }];
    const withBalance = pendingInvoices.filter(inv => {
      const paid = payments.filter(p => p.invoice_id === inv.id && !p.voided).reduce((s, p) => s + p.amount, 0);
      return inv.total - paid > 0;
    });
    if (withBalance.length === 0) return [{ value: '', label: 'Sin facturas pendientes' }];
    return [
      { value: '', label: 'Seleccionar...' },
      ...withBalance.map(inv => {
        const paid = payments.filter(p => p.invoice_id === inv.id && !p.voided).reduce((s, p) => s + p.amount, 0);
        const bal = Math.max(0, inv.total - paid);
        return { value: inv.id, label: `${inv.number} (${formatCurrency(bal)})` };
      }),
    ];
  }, [pendingInvoices, payments, pendingLoading]);

  const setPct = (pct: number) => {
    if (selectedInv && balance > 0) setAmount((balance * pct / 100).toFixed(2));
  };

  const numAmount = parseFloat(amount) || 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedInvoice || !amount || numAmount <= 0) {
      setError('Seleccioná una factura e ingresá un monto');
      setShowErrorModal(true);
      return;
    }
    if (numAmount > balance) {
      setError(`El monto no puede superar el saldo (${formatCurrency(balance)})`);
      setShowErrorModal(true);
      return;
    }
    setIsLoading(true);
    try {
      await addPayment({
        date,
        invoice_id: selectedInvoice,
        amount: numAmount,
        method,
        reference: reason.trim() || 'Nota de crédito',
        invoice_type: 'credit_note',
      });
      onClose();
    } catch (err: any) {
      setError(err.message || 'Error al emitir');
      setShowErrorModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Dialog.Root open={isOpen} onOpenChange={onClose}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50" />
          <Dialog.Content className="fixed left-1/2 top-1/2 w-[95vw] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white dark:bg-gray-900 shadow-xl overflow-hidden focus:outline-none">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Nota de crédito</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{entityName}</p>
                </div>
                <Dialog.Close asChild>
                  <button className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="Cerrar">
                    <X className="h-5 w-5" />
                  </button>
                </Dialog.Close>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="mb-4">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Factura *</label>
                <select value={selectedInvoice} onChange={(e) => handleInvoiceChange(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm"
                  disabled={pendingLoading}>
                  {invoiceOptions.map(o => (
                    <option key={o.value || 'opt'} value={o.value} disabled={o.value === '' && invoiceOptions.length > 1}>{o.label}</option>
                  ))}
                </select>
              </div>

              <div className="flex gap-3 mb-4">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Fecha</label>
                  <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm" />
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Monto *</label>
                  <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
                    placeholder="0" min="0.01" step="0.01" required
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-right"
                    disabled={!selectedInvoice || pendingLoading} />
                  {selectedInvoice && balance > 0 && (
                    <div className="flex gap-1 mt-1">
                      {[25, 50, 75, 100].map(p => (
                        <button key={p} type="button" onClick={() => setPct(p)} className="px-2 py-0.5 text-[10px] rounded bg-gray-100 dark:bg-gray-700 hover:bg-red-100 dark:hover:bg-red-900/30">
                          {p}%
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="mb-4">
                <button type="button" onClick={() => setShowAdvanced(!showAdvanced)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
                  {showAdvanced ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  Más opciones
                </button>
                {showAdvanced && (
                  <div className="mt-2 p-4 rounded-xl border border-gray-200 dark:border-gray-700 space-y-3">
                    <Select label="Método" value={method} onChange={(v) => setMethod(v as any)} options={[
                      { value: 'cash', label: 'Efectivo' }, { value: 'transfer', label: 'Transferencia' },
                      { value: 'card', label: 'Tarjeta' }, { value: 'check', label: 'Cheque' }, { value: 'mercadopago', label: 'MercadoPago' },
                    ]} />
                    <Input label="Motivo" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Opcional" />
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={onClose} className="flex-1">Cancelar</Button>
                <Button type="submit" disabled={!selectedInvoice || !amount || numAmount <= 0 || isLoading || pendingLoading} className="flex-1">
                  {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Emitir'}
                </Button>
              </div>
            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
      <ErrorModal isOpen={showErrorModal} onClose={() => setShowErrorModal(false)} message={error || 'Error'} />
    </>
  );
};

export default CreditNoteModal;
